import React, { useState } from 'react';
import { saveAs } from 'file-saver';
import { FormData } from './types/formTypes';
import { writeTrainingPlan, writeWorkerList } from './utils/excelUtils';
import BasicInfoForm from './components/BasicInfoForm';
import SubsidyCategoryForm from './components/SubsidyCategoryForm';
import TrainingInfoForm from './components/TrainingInfoForm';
import DetailedInfoForm from './components/DetailedInfoForm';
import WorkerListForm from './components/WorkerListForm';
import CareerUpPlanForm from './components/CareerUpPlanForm';
import './App.css';

const initialFormData: FormData = {
  companyName: '株式会社サンプル企業',
  insuranceNumber: '1234-567890-1',
  postalCode: '100-0001',
  address: '東京都千代田区千代田1-1-1 サンプルビル5F',
  contactPerson: {
    name: '田中太郎',
    department: '人事部',
    phone: '03-1234-5678',
    email: 'tanaka@sample-company.co.jp',
  },
  businessOwner: {
    postalCode: '100-0001',
    companyName: '株式会社サンプル企業',
    representativeName: '山田一郎',
    corporateNumber: '1234567890123',
  },
  subsidyCategory: '人材育成訓練',
  trainingCourseName: 'ITスキル向上研修',
  trainingPeriod: {
    startDate: '2024-07-01',
    endDate: '2024-09-30',
  },
  trainingLocation: {
    name: 'サンプル研修センター',
    address: '東京都新宿区新宿2-2-2 研修ビル3F',
  },
  trainingMethod: '通学制',
  trainingHours: {
    total: '120',
    academic: '80',
    practical: '40',
  },
  participants: {
    male: '3',
    female: '2',
  },
  hasQualificationExam: true,
  qualificationExamDetails: 'ITパスポート試験',
  qualificationExamDate: '2024-10-15',
  offJtType: '事業外',
  trainingInstitution: {
    name: 'サンプル教育訓練機関株式会社',
    representativeName: '教育太郎',
    address: '東京都渋谷区渋谷1-1-1 教育ビル10F',
    contractBackground: 'ITスキル向上のニーズに合致する訓練プログラムを提供していることから契約を締結した。',
    selectionTrigger: '自ら検索',
    loadReductionProposal: '受講料の分割払い対応、教材費無料化等の提案を受けた。',
    advertisingConfirmation: true,
    materialConfirmation: true,
    paymentConfirmation: true,
  },
  isDigitalTraining: true,
  digitalTrainingCategory: 'softwareEngineer',
  careerCenterName: '東京キャリア形成・学び直し支援センター',
  careerUpPlan: {
    hasCareerUpManager: true,
    careerUpManagerName: '人事部長 山田太郎',
    hasCareerUpPlan: true,
    implementationYear: '2025',
    implementationMonth: '4',
  },
  workers: [
    {
      id: '1',
      name: '佐藤花子',
      insuranceNumber: { part1: '1234', part2: '567890', part3: '1' },
      employmentType: '正規雇用労働者等',
      workerAttribute: '新規学卒予定者以外',
    },
    {
      id: '2',
      name: '鈴木一郎',
      insuranceNumber: { part1: '2345', part2: '678901', part3: '2' },
      employmentType: '正規雇用労働者等',
      workerAttribute: '新規学卒予定者以外',
    },
    {
      id: '3',
      name: '高橋美咲',
      insuranceNumber: { part1: '3456', part2: '789012', part3: '3' },
      employmentType: '有期契約労働者等',
      workerAttribute: '新規学卒予定者',
      expectedHireDate: {
        year: '2024',
        month: '4',
        day: '1',
      },
    },
  ],
};

function App() {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [isGenerating, setIsGenerating] = useState(false);

  const updateFormData = (updates: Partial<FormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const handleDownloadTrainingPlanExcelJS = async () => {
    try {
      setIsGenerating(true);
      const buffer = await writeTrainingPlan(formData);
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      saveAs(blob, '職業訓練実施計画届.xlsx');
    } catch (error) {
      console.error('Error generating training plan:', error);
      alert('職業訓練実施計画届の生成に失敗しました。');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadWorkerList = async () => {
    try {
      setIsGenerating(true);
      const buffer = await writeWorkerList(formData);
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      saveAs(blob, '対象労働者一覧.xlsx');
    } catch (error) {
      console.error('Error generating worker list:', error);
      alert('対象労働者一覧の生成に失敗しました。');
    } finally {
      setIsGenerating(false);
    }
  };


  return (
    <div className="App">
      <header className="App-header">
        <h1>人材開発支援助成金 申請書類作成システム</h1>
        <p>必要な情報を入力して、申請書類をExcelファイルとしてダウンロードできます。</p>
      </header>

      <main className="App-main">
        <form onSubmit={(e) => e.preventDefault()}>
          <BasicInfoForm formData={formData} updateFormData={updateFormData} />
          <SubsidyCategoryForm formData={formData} updateFormData={updateFormData} />
          <TrainingInfoForm formData={formData} updateFormData={updateFormData} />
          <DetailedInfoForm formData={formData} updateFormData={updateFormData} />
          <CareerUpPlanForm formData={formData} updateFormData={updateFormData} />
          <WorkerListForm formData={formData} updateFormData={updateFormData} />

          <div className="download-section">
            <h2>ダウンロード</h2>
            <div className="download-buttons">
              <button 
                type="button" 
                onClick={handleDownloadTrainingPlanExcelJS}
                disabled={isGenerating}
                className="download-button primary"
              >
                {isGenerating ? '生成中...' : '職業訓練実施計画届をダウンロード'}
              </button>
              
              <button 
                type="button" 
                onClick={handleDownloadWorkerList}
                disabled={isGenerating || formData.workers.length === 0}
                className="download-button"
              >
                {isGenerating ? '生成中...' : '対象労働者一覧をダウンロード'}
              </button>
            </div>
          </div>
        </form>
      </main>
    </div>
  );
}

export default App;
